import { CancelToken, isCancel } from "axios";
import _ from "lodash";

export function isReferredApplication(application) {
  return !!_.get(application, "eligibility.referredAt");
}

export function isEligibleInAts(application) {
  return (
    isReferredApplication(application) ||
    _.get(application, "eligibility.eligible") !== false
  );
}

export function isCompleteInAts(application) {
  return isReferredApplication(application) || application.complete !== false;
}

export function isVisibleInAts(application) {
  return isEligibleInAts(application) && isCompleteInAts(application);
}

const getDefaultState = () => {
  return {
    // All opportunity applications available on the server
    all: [],
    // The opportunity applications currently visible
    active: [],
    // What filters are currently applied
    filters: {
      categories: [],
      disciplines: [],
      ethnicities: [],
      genders: [],
      grade: [],
      institutions: [],
      location: "",
      locations: [],
      name: "",
      types: []
    },
    // Used to indicate when waiting on the server
    loading: false,
    // Value used to indicate progress while loading
    progress: null,
    // The current page being viewed
    page: 1,
    // Size of current page being viewed
    rowsPerPage: 25,
    // The search string being applied
    search: "",
    // Selected candidates used for bulk actions
    selected: [],
    // Sorting
    sort: {
      by: "createdAt",
      descending: false
    },
    // Token source used to abandon requests
    source: null,
    // The total records matching the filters (for pagination)
    total: 0
  };
};
export default {
  namespaced: true,
  state: getDefaultState(),
  getters: {
    find: state => id => state.all.find(application => application.id == id),
    hasHidden: state => state.active.some(application => !isVisibleInAts(application)),
    loading: state => state.loading,
    selected: state => state.selected,
    progress: state => {
      if (state.progress == null) {
        return 0;
      } else {
        return (state.progress[0] * 100.0) / state.progress[1];
      }
    },
    progressCurrent: state => (state.progress ? state.progress[0] : 0),
    progressTotal: state => (state.progress ? state.progress[1] : 0)
  },
  mutations: {
    setActive(
      state,
      {
        items = [],
        page = 1,
        per = 25,
        total = 0,
        search = null,
        sort = { by: "createdAt", descending: false },
        filters = {}
      }
    ) {
      state.search = search;
      state.active = items;
      state.sort = sort;
      state.filters = filters;
      state.page = page;
      state.rowsPerPage = per;
      state.total = total;
      state.loading = false;
      state.selected = [];
    },
    setAll(
      state,
      {
        items = [],
        page = 1,
        per = 25,
        total = 0,
        search = null,
        sort = { by: "createdAt", descending: false },
        filters = {}
      }
    ) {
      state.search = search;
      state.all = items;
      state.sort = sort;
      state.filters = filters;
      state.page = page;
      state.rowsPerPage = per;
      state.total = total;
      state.loading = false;
      state.selected = [];
    },
    set(state, application) {
      const find = state.all.find(o => o.id == application.id);
      if (find) state.all.splice(state.all.indexOf(find), 1, application);
      if (!find) state.all.push(application);
    },
    resetState(state) {
      Object.assign(state, getDefaultState());
    },
    select(state, value) {
      if (Array.isArray(value)) {
        for (let id of value) {
          if (state.selected.indexOf(id) < 0) state.selected.push(id);
        }
      } else state.selected.push(value);
    },
    deselect(state, value) {
      if (Array.isArray(value)) {
        state.selected = state.selected.filter(id => value.indexOf(id) == -1);
      } else state.selected = state.selected.filter(id => id !== value);
    },
    clearSelected(state) {
      state.selected = [];
    },
    append(state, { counts, applications }) {
      for (let application of applications) {
        const find = state.all.find(o => o.id === application.id);
        if (find) state.all.splice(state.all.indexOf(find), 1, application);
        if (!find) state.all.push(application);
        if (counts[application.status]) {
          counts[application.status]++;
        } else {
          counts[application.status] = 1;
        }
      }
    },
    setProgress(state, total = 0) {
      if (state.all.length == total) {
        state.progress = null; // done
      } else {
        state.progress = [state.all.length, total];
      }
    }
  },
  actions: {
    load(
      { commit, dispatch, state },
      { opportunity = Vue.$route.params.id } = {}
    ) {
      // Cancel previous load actions
      if (state.source) state.source.cancel();
      state.source = CancelToken.source();

      // Purge applications in case we switched opportunities
      state.all = state.all.filter(
        app => app.opportunity && app.opportunity.id === opportunity
      );
      dispatch("updateFilters", {});

      const pageSize = 250;
      state.loading = true;

      // First page builds the ID cache so subsequent pages only lookup applications
      return Vue.$http
        .get(`client/opportunities/${opportunity}/applications`, {
          params: { opportunity, sort: "id ASC", page: 1, per: pageSize },
          cancelToken: state.source.token
        })
        .then(
          async response => {
            if (response.data.items) {
              const total = response.data.total;
              var counts = {};
              // Set the total
              state.total = response.data.total;
              // Add the applications
              const applications = response.data.items;
              commit("append", { counts, applications });
              dispatch("updateFilters", {});
              commit("setProgress", total);
              // Update the phase counts
              commit("client/opportunities/setCounts", counts, { root: true });

              // Load the remainder synchronously
              if (total > pageSize) {
                const pages = Math.ceil(total / pageSize);
                for (let page = pages; page >= 2; page--) {
                  state.source = CancelToken.source();
                  let pageResponse = await Vue.$http.get(
                    `client/opportunities/${opportunity}/applications`,
                    {
                      params: {
                        opportunity,
                        sort: "id ASC",
                        page: page,
                        per: pageSize
                      },
                      cancelToken: state.source.token
                    }
                  );
                  if (pageResponse.data.items) {
                    commit("append", {
                      counts,
                      applications: pageResponse.data.items
                    });
                    dispatch("updateFilters", {});
                    commit("setProgress", total);
                    // Update the phase counts
                    commit("client/opportunities/setCounts", counts, {
                      root: true
                    });
                  }
                }
              }
              // Update the phase counts
              commit("client/opportunities/setCounts", counts, { root: true });
            }
            state.loading = false;
          },
          errors => {
            if (!isCancel(errors)) state.loading = false;
          }
        );
    },
    abandon({ state }, reason) {
      if (state.source) {
        state.source.cancel(reason);
        state.source = null;
      }
      state.loading = false;
    },
    search(
      { commit, state },
      {
        descending,
        filters,
        opportunity = Vue.$route.params.id,
        page,
        search,
        rowsPerPage,
        sortBy
      } = {}
    ) {
      if (state.source) {
        state.source.cancel();
      }
      state.source = CancelToken.source();
      state.filters = filters || state.filters;
      page = page || state.page;
      const per = rowsPerPage || state.rowsPerPage;
      search = search || state.search;
      sortBy = sortBy || state.sort.by;
      descending = descending == null ? state.sort.descending : descending;
      const sort = `${sortBy} ${descending ? "DESC" : "ASC"}`;

      state.loading = true;
      const api = opportunity
        ? `client/opportunities/${opportunity}/applications`
        : "client/applications";
      return Vue.$http
        .get(api, {
          params: { ...state.filters, page, search, sort, per },
          cancelToken: state.source.token
        })
        .then(
          response => commit("setAll", response.data),
          errors => {
            if (!isCancel(errors)) state.loading = false;
          }
        );
    },

    // Reflect filters changes client-side
    updateFilters({ commit, state }, assigned) {
      Object.assign(state.filters, { ...assigned });
      let { filters } = state;

      // Remove complete and referred filter if all applications are required
      if (filters["complete"] == false) delete filters["complete"];
      if (filters["referred"] == false) delete filters["referred"];

      // Set pagination
      if (assigned.page) state.page = assigned.page;
      if (assigned.rowsPerPage) state.rowsPerPage = assigned.rowsPerPage;

      // Set counts
      var counts = {};
      // Set current phase in case all have been moved out
      if (filters.status) counts[filters.status] = 0;

      // Apply filters from `all` to `active`
      state.active = applyFilters(state.all, state.filters, counts);
      // Set the total
      state.total = state.active.length;
      // Update the phase counts
      commit("client/opportunities/setCounts", counts, { root: true });
      // Sort results
      applySort(state.active, state.sort);
      // Paginate
      if (state.total < (state.page - 1) * state.rowsPerPage + 1)
        state.page = 1;
      state.active.splice(0, (state.page - 1) * state.rowsPerPage); // drop
      state.active.splice(
        state.rowsPerPage,
        state.active.length - state.rowsPerPage
      ); // take
    },

    updateSort({ dispatch, state }, sort) {
      state.sort = sort;
      dispatch("updateFilters", { page: 1 });
    },

    fetch({ commit, getters }, { id, view = false }) {
      const application = getters.find(id);

      if (application) {
        commit("set", Object.assign(application, { loading: true }));
      } else {
        commit("set", { id, loading: true });
      }

      const opportunityId = Vue.$route.params.id || application.opportunity.id;

      return Vue.$http
        .get(
          `client/opportunities/${opportunityId}/applications/${id}${
            view ? "?view=1" : ""
          }`
        )
        .then(response => {
          commit("set", Object.assign(response.data, { loading: false }));
          return response;
        });
    },

    move({ commit, dispatch }, { applications, phase }) {
      if (Array.isArray(applications)) {
        return Vue.$http
          .post(
            `client/opportunities/${Vue.$route.params.id}/applications/assign`,
            {
              phaseId: phase.id,
              opportunityApplicationIds: applications
            }
          )
          .then(() =>
            Promise.all(applications.map(id => dispatch("fetch", { id })))
          )
          .then(() => commit("deselect", applications));
      }

      const opportunityId = Vue.$route.params.id || applications.opportunity.id;

      return Vue.$http
        .post(
          `client/opportunities/${opportunityId}/applications/${applications.id}/events`,
          {
            phaseId: phase.id
          }
        )
        .then(() => dispatch("fetch", { id: applications.id }))
        .then(() => commit("deselect", applications.id));
    },
    regret({ commit, dispatch }, { applications }) {
      if (Array.isArray(applications)) {
        return Vue.$http
          .patch(
            `client/opportunities/${Vue.$route.params.id}/applications/regret`,
            {
              applicationIds: applications
            }
          )
          .then(() =>
            Promise.all(applications.map(id => dispatch("fetch", { id })))
          )
          .then(() => commit("deselect", applications));
      }

      const opportunityId = Vue.$route.params.id || applications.opportunity.id;

      return Vue.$http
        .patch(
          `client/opportunities/${opportunityId}/applications/${applications.id}/regret`
        )
        .then(() => dispatch("fetch", { id: applications.id }))
        .then(() => commit("deselect", applications.id));
    },

    resetState({ commit }) {
      commit("resetState");
    }
  }
};

function applySort(active, sort) {
  // Sort by ID
  if (sort.by == "id" && sort.descending == false)
    active.sort((a, b) => (a.id > b.id ? 1 : -1));
  if (sort.by == "id" && sort.descending == true)
    active.sort((a, b) => (a.id > b.id ? -1 : 1));

  // Sort by application date
  if (sort.by == "createdAt" && sort.descending == false)
    active.sort((a, b) =>
      new Date(a.createdAt) > new Date(b.createdAt) ? 1 : -1
    );
  if (sort.by == "createdAt" && sort.descending == true)
    active.sort((a, b) =>
      new Date(a.createdAt) > new Date(b.createdAt) ? -1 : 1
    );

  // Completion
  if (sort.by == "completion" && sort.descending == false)
    active.sort((a, b) => (a.completion > b.completion ? 1 : -1));
  if (sort.by == "completion" && sort.descending == true)
    active.sort((a, b) => (a.completion > b.completion ? -1 : 1));
  // Always show complete at the top
  if (sort.by == "completion")
    active.sort((a, b) =>
      a.complete === b.complete ? 0 : b.complete ? 1 : -1
    );

  // Name
  if (sort.by == "profile.lastName.sort" && sort.descending == false)
    active.sort((a, b) =>
      `${a.profile.lastName} ${a.profile.firstName}` >
      `${b.profile.lastName} ${b.profile.firstName}`
        ? 1
        : -1
    );
  if (sort.by == "profile.lastName.sort" && sort.descending == true)
    active.sort((a, b) =>
      `${a.profile.lastName} ${a.profile.firstName}` >
      `${b.profile.lastName} ${b.profile.firstName}`
        ? -1
        : 1
    );

  // Rating
  if (sort.by == "applicationRating.value" && sort.descending == false)
    active.sort((a, b) => (a.applicationRating > b.applicationRating ? 1 : -1));
  if (sort.by == "applicationRating.value" && sort.descending == true)
    active.sort((a, b) => (a.applicationRating > b.applicationRating ? -1 : 1));

  // Assessment
  if (sort.by == "profile.assessmentAggregate" && sort.descending == false)
    active.sort((a, b) =>
      a.psychometrics.reduce((acc, i) => acc + i.aggregate, 0) /
        (a.psychometrics.length || 1) >
      b.psychometrics.reduce((acc, i) => acc + i.aggregate, 0) /
        (b.psychometrics.length || 1)
        ? 1
        : -1
    );
  if (sort.by == "profile.assessmentAggregate" && sort.descending == true)
    active.sort((a, b) =>
      a.psychometrics.reduce((acc, i) => acc + i.aggregate, 0) /
        (a.psychometrics.length || 1) >
      b.psychometrics.reduce((acc, i) => acc + i.aggregate, 0) /
        (b.psychometrics.length || 1)
        ? -1
        : 1
    );
  // prioritise ones with more assessments
  if (sort.by == "profile.assessmentAggregate")
    active.sort((a, b) =>
      a.psychometrics.length > b.psychometrics.length ? -1 : 1
    );

  // Unread Messages
  if (sort.by == "unreadChats" && sort.descending == false)
    active.sort((a, b) => (a.unreadChats > b.unreadChats ? 1 : -1));
  if (sort.by == "unreadChats" && sort.descending == true)
    active.sort((a, b) => (a.unreadChats > b.unreadChats ? -1 : 1));

  // Ineligible always below eligible
  active.sort((a, b) =>
    isEligibleInAts(a) === isEligibleInAts(b)
      ? 0
      : isEligibleInAts(b)
      ? 1
      : -1
  );
  // Incomplete always below complete
  active.sort((a, b) =>
    isCompleteInAts(a) === isCompleteInAts(b)
      ? 0
      : isCompleteInAts(b)
      ? 1
      : -1
  );
}

// This takes a source collection of applications and returns only the ones
// that matches what is currently in the provided filters. Since filters affect
// the counts, these are also updated as a side-effect
function applyFilters(source, filters, counts) {
  return source.filter(application => {
    const today = startOfDay(new Date());

    // Initialize the counts before filtering applications
    if (!counts[application.status]) counts[application.status] = 0;

    // Only Complete
    if (filters.complete == true && application.complete !== true) return false;
    // Only Referred
    if (filters.referred == true && !application.eligibility.referredAt)
      return false;
    // Name
    if (
      filters.name &&
      filters.name.length &&
      `${application.profile.firstName} ${application.profile.lastName}`
        .toLowerCase()
        .indexOf(filters.name.toLowerCase()) == -1
    )
      return false;
    // Ethnicity
    if (
      filters.ethnicities &&
      filters.ethnicities.length &&
      !filters.ethnicities.includes(application.profile.ethnicity)
    )
      return false;
    // Gender
    if (
      filters.genders &&
      filters.genders.length &&
      !filters.genders.includes(application.profile.gender)
    )
      return false;
    // Opportunity Locations
    if (
      filters.locations &&
      filters.locations.length &&
      !filters.locations.some(
        id =>
          Array.isArray(application.locations) &&
          application.locations.some(l => l && l.id === id)
      )
    )
      return false;
    // Candidate Location
    if (
      filters.location &&
      filters.location.length &&
      filters.distance &&
      filters.distance > 0
    ) {
      const [lat, lon] = filters.location.split(",");
      if (!lat || !lon) return false; // need coordinates from location
      if (!application.profile.location) return false; // need candidate coordinates
      if (
        getDistanceFromLatLonInKm(
          lat,
          lon,
          application.profile.location.latitude,
          application.profile.location.longitude
        ) > filters.distance
      )
        return false;
    }

    /* Profile Qualifications */

    // Timeframes catch-all
    if (
      filters.include_completed ||
      filters.include_current ||
      filters.include_intended
    ) {
      const eligible = application.profile.profileQualifications.some(pq =>
        matchesSelectedTimeframes(pq, filters, today)
      );
      if (!eligible) return false;
    }

    // Fine-grained profile qualification checks
    if (
      (filters.grade &&
        filters.grade.length &&
        (filters.grade[0] > 0 || filters.grade[1] < 100)) ||
      (filters.institutions && filters.institutions.length) ||
      (filters.categories && filters.categories.length) ||
      (filters.types && filters.types.length) ||
      (filters.disciplines && filters.disciplines.length) ||
      filters.verified
    ) {
      if (
        application.profile.profileQualifications.filter(pq => {
          // Timeframe
          if (
            filters.include_completed ||
            filters.include_current ||
            filters.include_intended
          ) {
            if (!matchesSelectedTimeframes(pq, filters, today))
              return false;
          }

          // Grade
          if (
            filters.grade &&
            filters.grade.length == 2 &&
            (parseFloat(pq.gradeNormalized) <= filters.grade[0] ||
              parseFloat(pq.gradeNormalized) > filters.grade[1])
          )
            return false;
          // Institution
          if (
            filters.institutions &&
            filters.institutions.length &&
            !filters.institutions.includes(pq.institution.id)
          )
            return false;
          // Category
          if (
            filters.categories &&
            filters.categories.length &&
            !filters.categories.includes(
              pq.qualification.qualificationType.qualificationCategory.id
            )
          )
            return false;
          // Type
          if (
            filters.types &&
            filters.types.length &&
            !filters.types.includes(pq.qualification.qualificationType.id)
          )
            return false;
          // Discipline
          if (filters.disciplines && filters.disciplines.length) {
            const pqDiscIds = Array.isArray(pq.disciplines)
              ? pq.disciplines.map(d => d && d.id).filter(id => id != null)
              : [];
            const hasAny = filters.disciplines.some(id =>
              pqDiscIds.includes(id)
            );
            if (!hasAny) return false;
          }

          // Verified
          if (filters.verified && !pq.verifiedAt) return false;

          return true;
        }).length == 0
      )
        return false;
    }

    // Update counts
    if (counts[application.status]) {
      counts[application.status]++;
    } else {
      counts[application.status] = 1;
    }

    // Status (after counts to update them all)
    if (filters["status"] && application.status != filters["status"])
      return false;

    return true;
  });
}

function parseDate(value) {
  if (!value) return null;

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function startOfDay(value) {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
}

function isCompletedQualification(pq, today) {
  const endsAt = parseDate(pq.endsAt);
  return !!endsAt && startOfDay(endsAt) < today;
}

function isCurrentQualification(pq, today) {
  const startsAt = parseDate(pq.startsAt);
  if (!startsAt) return false;

  const endsAt = parseDate(pq.endsAt);
  return startOfDay(startsAt) <= today && (!endsAt || startOfDay(endsAt) >= today);
}

function isIntendedQualification(pq, today) {
  const startsAt = parseDate(pq.startsAt);
  return !!startsAt && startOfDay(startsAt) > today;
}

function matchesSelectedTimeframes(pq, filters, today) {
  return (
    (filters.include_completed && isCompletedQualification(pq, today)) ||
    (filters.include_current && isCurrentQualification(pq, today)) ||
    (filters.include_intended && isIntendedQualification(pq, today))
  );
}

function getDistanceFromLatLonInKm(lat1, lon1, lat2, lon2) {
  var R = 6371; // Radius of the earth in km
  var dLat = deg2rad(lat2 - lat1); // deg2rad below
  var dLon = deg2rad(lon2 - lon1);
  var a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) *
      Math.cos(deg2rad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  var d = R * c; // Distance in km
  return d;
}

function deg2rad(deg) {
  return deg * (Math.PI / 180);
}
